import { type ChangeEvent, type JSX, useEffect, useMemo, useState } from "react"
import { createRoot } from "react-dom/client"
import { Analytics } from "lib/analytics"

type FaqItem = {
  question: string
  answer_html: string
}

type PreparedFaqItem = FaqItem & {
  searchText: string
}

function FaqApp({ items }: { items: FaqItem[] }): JSX.Element {
  const [query, setQuery] = useState("")
  const trimmedQuery = query.trim()

  const preparedItems = useMemo<PreparedFaqItem[]>(() => {
    return items.map((item) => ({
      ...item,
      searchText: `${item.question} ${htmlToText(item.answer_html)}`.toLowerCase(),
    }))
  }, [items])

  const visibleItems = useMemo(() => {
    if (trimmedQuery.length === 0) return preparedItems

    const needle = trimmedQuery.toLowerCase()
    return preparedItems.filter((item) => item.searchText.includes(needle))
  }, [preparedItems, trimmedQuery])

  useEffect(() => {
    if (trimmedQuery.length === 0) return

    const timeout = window.setTimeout(() => {
      Analytics.track("faq_query", { query: trimmedQuery })
    }, 600)

    return () => window.clearTimeout(timeout)
  }, [trimmedQuery])

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>): void {
    setQuery(event.currentTarget.value)
  }

  return (
    <>
      <div id="top_area">
        <h1>Frequently Asked Questions</h1>

        <div id="faq_search_container">
          <div className="fixed-width">
            <input
              id="faq_search"
              type="text"
              placeholder="Enter a search term to highlight answers"
              value={query}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      <div id="faq_answers_area" className="link-cards">
        <div className="fixed-width">
          {visibleItems.map((item) => (
            <FaqQuestionAndAnswer
              key={item.question}
              item={item}
              query={trimmedQuery}
            />
          ))}

          {visibleItems.length === 0 ? (
            <p className="faq-empty" role="status">
              No FAQ answers match your search.
            </p>
          ) : null}
        </div>
      </div>
    </>
  )
}

function FaqQuestionAndAnswer({ item, query }: { item: FaqItem; query: string }): JSX.Element {
  return (
    <div className="faq-question-and-answer">
      <h2 className="faq-question">{highlightText(item.question, query)}</h2>
      <div
        className="faq-answer"
        dangerouslySetInnerHTML={{ __html: highlightHtml(item.answer_html, query) }}
      />
    </div>
  )
}

export function mountFaqApp(): void {
  const mountEl = document.getElementById("faq-react-app")
  const dataEl = document.getElementById("faq-data")

  if (!mountEl || !dataEl?.textContent) return

  const items = JSON.parse(dataEl.textContent) as FaqItem[]
  createRoot(mountEl).render(<FaqApp items={items} />)
}

function highlightText(source: string, query: string): Array<string | JSX.Element> {
  if (query.length === 0) return [source]

  const pattern = new RegExp(escapeRegex(query), "gi")
  const nodes: Array<string | JSX.Element> = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  while ((match = pattern.exec(source)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(source.slice(lastIndex, match.index))
    }

    nodes.push(
      <mark className="faq-highlight" key={key}>
        {match[0]}
      </mark>,
    )
    key += 1
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < source.length) {
    nodes.push(source.slice(lastIndex))
  }

  return nodes
}

function highlightHtml(html: string, query: string): string {
  if (query.length === 0) return html

  const document = new DOMParser().parseFromString(html, "text/html")
  highlightTextNodes(document.body, query)
  return document.body.innerHTML
}

function highlightTextNodes(root: HTMLElement, query: string): void {
  const pattern = new RegExp(escapeRegex(query), "gi")

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT

      const tag = parent.tagName
      if (tag === "SCRIPT" || tag === "STYLE" || tag === "MARK") {
        return NodeFilter.FILTER_REJECT
      }

      pattern.lastIndex = 0
      return pattern.test(node.textContent ?? "")
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT
    },
  })

  const matchingNodes: Text[] = []
  let node: Node | null

  while ((node = walker.nextNode())) {
    matchingNodes.push(node as Text)
  }

  for (const textNode of matchingNodes) {
    pattern.lastIndex = 0
    const source = textNode.textContent ?? ""
    const fragment = document.createDocumentFragment()
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = pattern.exec(source)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(source.slice(lastIndex, match.index)))
      }

      const mark = document.createElement("mark")
      mark.className = "faq-highlight"
      mark.textContent = match[0]
      fragment.appendChild(mark)
      lastIndex = match.index + match[0].length
    }

    if (lastIndex < source.length) {
      fragment.appendChild(document.createTextNode(source.slice(lastIndex)))
    }

    textNode.parentNode?.replaceChild(fragment, textNode)
  }
}

function htmlToText(html: string): string {
  return new DOMParser().parseFromString(html, "text/html").body.textContent ?? ""
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

